/**
 * Which recording the home screen is showing while the length and cadence move under it.
 *
 * The preview used to be a screen of its own, drawn once for one length. On the home
 * screen it redraws on every tap of a length chip and on every tick of a fixed finish
 * time, and drawing the recording again each time would be wrong twice over. The draw is
 * random, so the title would change on every tap. And the band exists to show one thing —
 * his speech holding still while the silences open and close — which can only be seen if
 * it is the same speech from one length to the next. So a recording stays while it can
 * still make the length, and a new one is drawn only when it cannot.
 */

import type { ChoiceReason } from './preview-schema';
import type { Manifest, Recording, SessionPlan } from '../types';
import { isEligible, evaluateSession, IneligibleSessionError } from './eligibility';
import { compositionSourceKey } from './composition';
import { planFingerprint } from './plan';
import { NoCandidatesError, selectRecording, type SelectionContext } from './select';
import { isWithheld } from './withheld';

export interface Choice {
  recording: Recording;
  /**
   * Chosen from the library by hand. A hand-picked recording is never swapped out behind
   * someone's back: if the length moves past what it can do, the home screen says so and
   * leaves "Something else" to them.
   */
  picked: boolean;
}

export function drawChoice(manifest: Manifest, ctx: SelectionContext): Choice {
  return { recording: selectRecording(manifest, ctx).chosen, picked: false };
}

export function pickChoice(recording: Recording): Choice {
  return { recording, picked: true };
}

/** The current release's copy of the choice if it still fits; otherwise a fresh draw. */
export function keepOrDraw(
  previous: Choice | null, manifest: Manifest, ctx: SelectionContext,
  onReason?: (reason: ChoiceReason) => void,
): Choice {
  if (!previous) { onReason?.('initial-draw'); return drawChoice(manifest, ctx); }

  // A cached manifest can put a choice on screen before the fresh release arrives. Every
  // decision below must use the release's object: the same id may have new edit bounds,
  // paths or policy, and an id absent here is no longer playable from this release.
  const current = manifest.recordings.find((recording) => recording.id === previous.recording.id);
  if (!current || isWithheld(current.id)) {
    onReason?.(!current ? 'removed' : 'withheld');
    return drawChoice(manifest, ctx);
  }

  if (previous.picked || isEligible({
    recording: current, targetSec: ctx.targetSec, cadence: ctx.cadence,
    toleranceSec: ctx.toleranceSec, desiredSpeechRatio: ctx.desiredSpeechRatio,
  })) {
    onReason?.('kept');
    return current === previous.recording ? previous : { recording: current, picked: previous.picked };
  }
  onReason?.(ctx.cadence !== 'opening' && isEligible({recording: current,
    targetSec: ctx.targetSec, cadence: 'opening', toleranceSec: ctx.toleranceSec,
    desiredSpeechRatio: ctx.desiredSpeechRatio}) ? 'cadence-unavailable' : 'duration-miss');
  return drawChoice(manifest, ctx);
}

/** An explicit exclusion makes Something else distinct even on an adversarial random stream. */
export function drawAnother(
  previous: Choice | null, manifest: Manifest, ctx: SelectionContext,
  onReason?: (reason: ChoiceReason) => void,
): Choice {
  onReason?.('explicit-another');
  if (!previous) return drawChoice(manifest,ctx);
  try { return drawChoice(manifest,{...ctx,excludeIds:[...(ctx.excludeIds ?? []),previous.recording.id]}); }
  catch(error) {
    if (!(error instanceof NoCandidatesError) || error.reason !== 'duration') throw error;
    return drawChoice(manifest,ctx);
  }
}

/** The first plan shown in each preset cell lives only for this home visit. */
export function createHomeChoiceMemory() {
  const cells = new Map<string,{choice:Choice;plan:SessionPlan;sourceKey:string;fingerprint:string}>();
  return {
    clear() { cells.clear(); },
    resolve(previous:Choice|null,manifest:Manifest,ctx:SelectionContext,deadline=false,onReason?:(reason:ChoiceReason)=>void) {
      const key=`${ctx.targetSec}/${ctx.cadence}`;
      if(!deadline && !previous?.picked) {
        const saved=cells.get(key);
        if(saved) {
          const current=manifest.recordings.find(r=>r.id===saved.choice.recording.id);
          if(current && compositionSourceKey(current)===saved.sourceKey) {
            const result=evaluateSession({recording:current,targetSec:ctx.targetSec,cadence:ctx.cadence,
              toleranceSec:ctx.toleranceSec,desiredSpeechRatio:ctx.desiredSpeechRatio});
            if(result.eligible && JSON.stringify(planFingerprint(result.plan,current))===saved.fingerprint) {
              onReason?.('restored');return {choice:{recording:current,picked:false},plan:saved.plan};
            }
          }
          cells.delete(key);
        }
      }
      // A deadline does not create per-second cells. Keep today's stay-put decision.
      const choice=keepOrDraw(previous,manifest,ctx,onReason);
      const result=evaluateSession({recording:choice.recording,targetSec:ctx.targetSec,cadence:ctx.cadence,
        toleranceSec:ctx.toleranceSec,desiredSpeechRatio:ctx.desiredSpeechRatio});
      if(!result.eligible) throw new IneligibleSessionError(result);
      if(!deadline && !choice.picked) cells.set(key,{choice,plan:result.plan,
        sourceKey:compositionSourceKey(choice.recording),fingerprint:JSON.stringify(planFingerprint(result.plan,choice.recording))});
      return {choice,plan:result.plan};
    },
  };
}
